import type { Editor as EditorType, EditorEvent } from 'hugerte';

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const getNextFocusableAfter = (container: HTMLElement): HTMLElement | null => {
	const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
		acceptNode: (node) => {
			if (!(node instanceof HTMLElement)) return NodeFilter.FILTER_SKIP;
			if (container.contains(node)) return NodeFilter.FILTER_REJECT;
			return node.matches(FOCUSABLE_SELECTOR) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
		},
	});
	walker.currentNode = container;
	const next = walker.nextNode();
	return next instanceof HTMLElement ? next : null;
};

/**
 * HugeRte renders its editor inside a container with `role="application"`, which
 * puts screen readers into Focus Mode and swallows key navigation. ARIA guidance
 * for the `application` role requires that pressing Escape returns the user to
 * Browse Mode, which here means moving focus out of the editor entirely.
 *
 * HugeRte's default Escape behavior keeps focus inside the editor (toggling
 * between toolbar and content). This helper overrides that so Escape moves
 * focus to the next tabbable element after the editor container (or `body`
 * as a fallback).
 *
 * Implementation notes:
 *
 * - The editor body lives inside an iframe. Native capture-phase listeners on
 *   the iframe document don't reliably stop HugeRte from re-asserting focus,
 *   so we register through HugeRte's own event system with `prepend = true`,
 *   which puts our handler at the front of HugeRte's keydown chain.
 * - The toolbar lives in the parent document. We register a capture-phase
 *   listener on the container so we run before HugeRte's bubble-phase Alloy
 *   handlers.
 * - To force focus out of the iframe we temporarily set its `tabindex` to -1
 *   and blur it before focusing the next element, then restore the original
 *   tabindex on the next animation frame. Calling `.focus()` alone often
 *   leaves focus stuck inside the iframe because the browser snaps it back
 *   to the iframe element.
 * - Escape inside a HugeRte dialog (e.g. the link editor) is left alone so
 *   the dialog can close itself.
 */
export const attachExitOnEscape = (editor: EditorType): void => {
	const container = editor.getContainer();

	const moveFocusOutOfEditor = (): void => {
		const next = getNextFocusableAfter(container) ?? document.body;
		const iframe = editor.iframeElement;

		if (!iframe) {
			next.focus();
			return;
		}

		const previousTabIndex = iframe.getAttribute('tabindex');
		iframe.setAttribute('tabindex', '-1');
		iframe.blur();
		next.focus();

		requestAnimationFrame(() => {
			if (previousTabIndex === null) {
				iframe.removeAttribute('tabindex');
			} else {
				iframe.setAttribute('tabindex', previousTabIndex);
			}
		});
	};

	const handleEditorKeydown = (event: EditorEvent<KeyboardEvent>): void => {
		if (event.key !== 'Escape') return;
		event.preventDefault();
		event.stopImmediatePropagation();
		moveFocusOutOfEditor();
	};

	const handleContainerKeydown = (event: KeyboardEvent): void => {
		if (event.key !== 'Escape') return;
		if (event.target instanceof HTMLElement && event.target.closest('[role="dialog"]')) return;
		event.preventDefault();
		event.stopImmediatePropagation();
		moveFocusOutOfEditor();
	};

	editor.on('keydown', handleEditorKeydown, true);
	container.addEventListener('keydown', handleContainerKeydown, true);

	editor.once('remove', () => {
		editor.off('keydown', handleEditorKeydown);
		container.removeEventListener('keydown', handleContainerKeydown, true);
	});
};
